import {
  Children,
  createContext,
  isValidElement,
  memo,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react'
import cn from 'classnames'

import { ScreenLoadGate } from '~/shared/lib/screenLoad'
import { ScreenBackground } from '~/shared/ui/ScreenBackground'

import { FOOTER_HEIGHT_VAR } from './config'
import type {
  ScreenContentProps,
  ScreenFooterProps,
  ScreenHeaderSlotProps,
  ScreenProps,
} from './types'

import styles from './Screen.module.scss'

type ScreenContextValue = {
  hasMenu: boolean
  hasStickyCta: boolean
  hasHeader: boolean
  /** Сообщает оболочке высоту закреплённого футера; null — футера больше нет. */
  setFooterHeight: (height: number | null) => void
}

const ScreenContext = createContext<ScreenContextValue>({
  hasMenu: false,
  hasStickyCta: false,
  hasHeader: false,
  setFooterHeight: () => {},
})

const Header = memo(function ScreenHeaderSlot(props: ScreenHeaderSlotProps) {
  return <header className={styles.header}>{props.children}</header>
})

/**
 * Каркас экрана: фоновые пятна + вертикальная раскладка шапка/контент/футер.
 * Контент скроллится (scroll-area), шапка и футер — нет.
 */
const ScreenRoot = memo(function Screen(props: ScreenProps) {
  const { background = 'HOME', hasMenu = false, hasStickyCta = false, children } = props

  const rootRef = useRef<HTMLDivElement>(null)

  // Высота футера едет к контенту переменной, а не состоянием: она меняется на каждом
  // изменении раскладки футера (появилась вторая кнопка, сменился текст в две строки),
  // и пересобирать из-за этого весь экран незачем.
  const setFooterHeight = useCallback((height: number | null) => {
    const root = rootRef.current
    if (!root) return

    if (height === null) {
      root.style.removeProperty(FOOTER_HEIGHT_VAR)
      return
    }

    root.style.setProperty(FOOTER_HEIGHT_VAR, `${Math.ceil(height)}px`)
  }, [])

  // Верхний отступ экрана даёт либо шапка, либо сам контент — но всегда ровно один из них.
  // Без этого экраны без шапки прилипали к верхнему краю, а отступы разъезжались от экрана
  // к экрану (спека 01 §3: шапка 22–26px сверху).
  const hasHeader = useMemo(
    () =>
      Children.toArray(children).some((child) => isValidElement(child) && child.type === Header),
    [children],
  )

  const context = useMemo(
    () => ({ hasMenu, hasStickyCta, hasHeader, setFooterHeight }),
    [hasMenu, hasStickyCta, hasHeader, setFooterHeight],
  )

  return (
    <ScreenContext.Provider value={context}>
      <div className={styles.root} ref={rootRef}>
        {/* Спека 01 §6: анимация звёзд — только на полотне «Узы», иначе фон статичный. */}
        <ScreenBackground variant={background} showStars={background === 'CANVAS'} />
        <div className={styles.frame}>{children}</div>
      </div>
    </ScreenContext.Provider>
  )
})

const Content = memo(function ScreenContent(props: ScreenContentProps) {
  const { padding = 'DEFAULT', children } = props
  const { hasMenu, hasStickyCta, hasHeader } = useContext(ScreenContext)

  // Гейт готовности стоит здесь, а не на каждой странице: экраны собираются из независимых
  // виджетов со своими запросами, и «подпрыгивание после лоадинга» иначе приходилось бы
  // ловить по одному. Экранам без запросов он не мешает — те показываются сразу.
  // BLEED — это один виджет во всю высоту слота (полотно уз) со своим состоянием загрузки.
  // Гейта тут нет намеренно: его обёртки ломают цепочку процентных высот, и полотно, которое
  // тянется на `height: 100%`, схлопывалось в ноль — экран уз оказывался пустым.
  if (padding === 'BLEED') {
    return <main className={cn(styles.content, styles.bleed)}>{children}</main>
  }

  return (
    <main
      className={cn(
        styles.content,
        !hasHeader && styles.withTopPadding,
        // Классы не исключают друг друга: когда футер стоит над меню, снизу два препятствия
        // подряд, и резерв под ними складывается (см. Screen.module.scss).
        hasStickyCta && styles.reserveCta,
        hasMenu && styles.reserveMenu,
      )}
    >
      <ScreenLoadGate>{children}</ScreenLoadGate>
    </main>
  )
})

const Footer = memo(function ScreenFooter(props: ScreenFooterProps) {
  const { hasMenu, setFooterHeight } = useContext(ScreenContext)
  const footerRef = useRef<HTMLElement>(null)

  // Высоту футера меряем, а не задаём числом: под кнопкой бывает вторая кнопка, подпись или
  // ничего, и подобранный резерв под контентом каждый раз оказывался либо мал (контент
  // уезжал под кнопку), либо велик (внизу зияла пустота).
  useLayoutEffect(() => {
    const node = footerRef.current
    if (!node) return undefined

    const publish = () => setFooterHeight(node.getBoundingClientRect().height)
    publish()

    // ResizeObserver есть не везде (jsdom в тестах) — там достаточно одного замера.
    if (typeof ResizeObserver === 'undefined') return () => setFooterHeight(null)

    const observer = new ResizeObserver(publish)
    observer.observe(node)

    return () => {
      observer.disconnect()
      setFooterHeight(null)
    }
  }, [setFooterHeight])

  return (
    <footer ref={footerRef} className={cn(styles.footer, hasMenu && styles.aboveMenu)}>
      {props.children}
    </footer>
  )
})

export const Screen = Object.assign(ScreenRoot, { Header, Content, Footer })
